import { useEffect, useState } from "react";

/** Visual segment for the bar: label, width percentage, and fill color class. */
export type SegmentedBarSegment = {
  label: string;
  pct: number;
  fill: string;
};

export function SegmentedBar({
  segments,
  animate = false,
}: {
  segments: SegmentedBarSegment[];
  animate?: boolean;
}) {
  // Zero-width segments are dropped: flex `gap` would still reserve 2px for them.
  const visible = segments.filter((seg) => seg.pct > 0);
  const [grown, setGrown] = useState(!animate);

  useEffect(() => {
    if (grown) return;
    // Double rAF so the 0% state is painted before we transition to the real widths.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setGrown(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [grown]);

  const ariaLabel = visible.map((s) => `${s.pct}% ${s.label}`).join(", ");

  return (
    <div className="flex w-full gap-0.5 h-5" role="img" aria-label={ariaLabel}>
      {visible.map((seg, i) => (
        <div
          key={i}
          className={`h-full min-w-px transition-[flex-basis] duration-500 ease-out ${seg.fill} ${i === 0 ? "rounded-l" : ""} ${i === visible.length - 1 ? "rounded-r" : ""}`}
          style={{
            flexBasis: grown ? `${seg.pct}%` : "0%",
          }}
        />
      ))}
    </div>
  );
}
